<template>
  <nav class="nav">
    <div class="wrap">
      <!-- LEFT: Logo -->
      <div class="left">
        <img :src="logoSrc" alt="Logo" class="logo" />
      </div>

      <!-- CENTER -->
      <div class="center">
        <!-- Dashboard pill -->
        <router-link
          :to="dashboardItem.to"
          class="singlePill"
          :class="isActive(dashboardItem.to) ? 'singlePillActive' : ''"
        >
          {{ dashboardItem.label }}
        </router-link>

        <!-- Main big pill -->
        <div class="mainPill">
          <ul class="mainList">
            <li v-for="(item, i) in mainItems" :key="item.key" class="li">
              <router-link
                v-if="item.isPage"
                :to="item.links[0].to"
                class="pillLink"
                :class="isActive(item.links[0].to) ? 'pillLinkActive' : ''"
              >
                {{ item.label }}
              </router-link>

              <template v-else>
                <button
                  type="button"
                  class="pillLink btn"
                  @click.stop="toggleItem(i)"
                  :aria-expanded="openIndex === i"
                  :aria-controls="`dd-${i}`"
                >
                  <span>{{ item.label }}</span>
                  <svg
                    class="caret"
                    :class="openIndex === i ? 'caretUp' : ''"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                <ul
                  :id="`dd-${i}`"
                  v-show="openIndex === i"
                  class="dropdown"
                  @mouseenter="cancelClose(i)"
                  @mouseleave="scheduleClose(i)"
                  @focusin="cancelClose(i)"
                  @focusout="scheduleClose(i)"
                >
                  <li v-for="link in item.links" :key="link.text">
                    <router-link
                      :to="link.to"
                      class="dropdownItem"
                      @click="handleDropdownLinkClick"
                    >
                      {{ link.text }}
                    </router-link>
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="right">
        <!-- Language -->
        <button type="button" class="singlePill lang" @click="toggleLang">
          {{ lang }}
        </button>

        <!-- Profile -->
        <div
          class="profileWrap"
          @mouseenter="onProfileEnter"
          @mouseleave="onProfileLeave"
          @focusin="onProfileEnter"
          @focusout="onProfileLeave"
        >
          <button
            id="profile-trigger"
            type="button"
            class="profilePill"
            @click.stop="profileOpen = !profileOpen"
            :aria-expanded="profileOpen"
            aria-controls="profile-dd"
          >
            <!-- person icon -->
            <svg
              class="person"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.51 4.51 0 0 0 12 12Z" />
              <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
            </svg>

            <!-- caret -->
            <svg
              class="caret"
              :class="profileOpen ? 'caretUp' : ''"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          <!-- profile dropdown -->
          <div
            v-show="profileOpen"
            id="profile-dd"
            class="dropdown profileDd"
            @mouseenter="cancelProfileClose"
            @mouseleave="scheduleProfileClose"
          >
            <div class="userName">{{ userName || "User Name" }}</div>

            <button type="button" class="menuRow" @click="logout">
              <svg
                viewBox="0 0 24 24"
                class="logoutIcon"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M15 12H3" />
                <path d="M11 8l-4 4 4 4" />
                <path d="M15 4h5v16h-5" />
              </svg>
              <span class="menuText">Log Out</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile hamburger -->
      <button
        class="burger"
        @click="menuOpen = !menuOpen"
        aria-label="Toggle navigation"
      >
        <svg
          class="burgerIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-width="2"
            d="M4 7h16M4 12h16M4 17h16"
          />
        </svg>
      </button>
    </div>

    <!-- Mobile sheet -->
    <div v-if="menuOpen" class="mobile">
      <div class="mobileCard">
        <router-link
          :to="dashboardItem.to"
          class="mobileLink"
          @click="menuOpen = false"
        >
          {{ dashboardItem.label }}
        </router-link>

        <router-link
          v-for="it in flatMobileItems"
          :key="it.to"
          :to="it.to"
          class="mobileLink"
          @click="menuOpen = false"
        >
          {{ it.text }}
        </router-link>

        <button class="mobileLink btnReset" @click="toggleLang">
          {{ lang }}
        </button>
        <button class="mobileLink danger" @click="logout">Logout</button>
      </div>
    </div>
  </nav>
</template>

<script>
import axios from "axios";

// ✅ axios instance بدل "/api"
const api = axios.create({
  baseURL: "https://booking.lyon-jo.com/api",
});

// ✅ ضيف Authorization إذا عندك token
api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default {
  name: "Navbar",
  data() {
    return {
      // ✅ Webpack 4 compatible
      logoSrc: require("../assets/Navbarlogo.png"),

      menuOpen: false,
      profileOpen: false,
      openIndex: null,
      timers: {},
      closeDelay: 220,
      profileTimer: null,

      userName: localStorage.getItem("user_name") || "User",
      lang: "EN",

      dashboardItem: { key: "dashboard", label: "Dashboard", to: "/dashboard" },
      mainItems: [
        {
          key: "customers",
          label: "Customers",
          isPage: true,
          links: [{ to: "/customers", text: "Customers" }],
        },
        {
          key: "others",
          label: "Others",
          isPage: false,
          links: [
            { to: "/permissions", text: "Permitions" },
            { to: "/admin-logs", text: "Admin logs" },
          ],
        },
        {
          key: "whatsapp",
          label: "WhatsApp",
          isPage: false,
          links: [
            { to: "/whatsapp-templates", text: "Template Management" },
            { to: "/send-whatsapp", text: "Send via WhatsApp" },
            { to: "/whatsapp-inbox", text: "Inbox" },
          ],
        },
        {
          key: "engagement",
          label: "User Engagement",
          isPage: true,
          links: [{ to: "/user-engagement", text: "Engagement" }],
        },
      ],
    };
  },
  computed: {
    flatMobileItems() {
      const out = [];
      this.mainItems.forEach((it) => {
        if (it.isPage) out.push({ to: it.links[0].to, text: it.label });
        else it.links.forEach((l) => out.push({ to: l.to, text: l.text }));
      });
      return out;
    },
  },
  methods: {
    isActive(path) {
      return this.$route?.path === path;
    },

    toggleLang() {
      this.lang = this.lang === "EN" ? "AR" : "EN";
      this.profileOpen = false;
    },

    handleDropdownLinkClick() {
      this.openIndex = null;
    },

    clearTimer(i) {
      if (this.timers[i]) {
        clearTimeout(this.timers[i]);
        this.timers[i] = null;
      }
    },
    closeItem(i) {
      if (this.openIndex === i) this.openIndex = null;
    },
    toggleItem(i) {
      this.clearTimer(i);
      this.openIndex = this.openIndex === i ? null : i;
    },
    cancelClose(i) {
      this.clearTimer(i);
    },
    scheduleClose(i) {
      this.clearTimer(i);
      this.timers[i] = setTimeout(() => this.closeItem(i), this.closeDelay);
    },

    onProfileEnter() {
      this.cancelProfileClose();
      this.profileOpen = true;
    },
    onProfileLeave() {
      this.scheduleProfileClose();
    },
    scheduleProfileClose() {
      this.clearProfileTimer();
      this.profileTimer = setTimeout(
        () => (this.profileOpen = false),
        this.closeDelay,
      );
    },
    cancelProfileClose() {
      this.clearProfileTimer();
    },
    clearProfileTimer() {
      if (this.profileTimer) {
        clearTimeout(this.profileTimer);
        this.profileTimer = null;
      }
    },

    async logout() {
      try {
        await api.post("/logout");
      } catch (e) {
        console.error("Logout failed:", e?.response || e?.message);
      } finally {
        // نفس منطق الجديد
        const tenant = localStorage.getItem("hotels_tenant");
        localStorage.clear();
        if (tenant) localStorage.setItem("hotels_tenant", tenant);

        this.menuOpen = false;
        this.profileOpen = false;
        this.openIndex = null;

        this.$router.push({ name: "Login" }).catch(() => {});
      }
    },

    onDocClick(e) {
      if (!this.$el.contains(e.target)) {
        this.openIndex = null;
        this.profileOpen = false;
      }
    },
    onEsc(e) {
      if (e.key === "Escape") {
        this.openIndex = null;
        this.profileOpen = false;
        this.menuOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocClick);
    document.addEventListener("keydown", this.onEsc);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocClick);
    document.removeEventListener("keydown", this.onEsc);
  },
};
</script>

<style scoped>
.nav {
  padding: 16px;
  background: #fff;
  position: relative;
  z-index: 1000;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

/* layout */
.wrap {
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.logo {
  width: 93px;
  height: 47px;
  object-fit: contain;
  border-radius: 4px;
  user-select: none;
}

.center {
  display: none;
  flex: 1;
  justify-content: flex-end;
  min-width: 0;
  gap: 16px;
  align-items: center;
}
.right {
  display: none;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.singlePill {
  height: 59px;
  padding: 0 18px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  user-select: none;
  transition: 0.15s ease;
  border: 1px solid #c9d5df;
  background: linear-gradient(to bottom, #d9e8f2, #c3d2de);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  text-decoration: none;
}
.singlePill:hover {
  filter: brightness(1.02);
}
.singlePillActive {
  background: linear-gradient(to bottom, #e3f0f9, #c3d2de);
}

.mainPill {
  height: 59px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  min-width: 0;
  border: 1px solid #c9d5df;
  background: linear-gradient(to bottom, #d9e8f2, #c3d2de);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
}
.mainList {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 32px;
  list-style: none;
  margin: 0;
  white-space: nowrap;
}
.li {
  position: relative;
}

.pillLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 20px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  transition: 0.15s ease;
  user-select: none;
  text-decoration: none;
}
.pillLink:hover {
  color: #111827;
  background: rgba(255, 255, 255, 0.35);
}
.pillLinkActive {
  background: rgba(255, 255, 255, 0.45);
}
.btn {
  background: transparent;
  border: 0;
  cursor: pointer;
  gap: 8px;
}

.caret {
  width: 16px;
  height: 16px;
  color: #374151;
  transition: transform 0.15s ease;
}
.caretUp {
  transform: rotate(180deg);
}

/* dropdown */
.dropdown {
  position: absolute;
  top: 120%;
  min-width: 176px;
  border-radius: 12px;
  padding: 8px 0;
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.1);
  z-index: 1100;
  background: linear-gradient(to top, #dee0e3 0%, #f9fafb 60%, #ffffff 100%);
}
.dropdownItem {
  display: block;
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
  border-radius: 10px;
  transition: 0.15s ease;
  text-decoration: none;
}
.dropdownItem:hover {
  background: #709ebd;
  color: #fff;
}

.profileWrap {
  position: relative;
}
.profilePill {
  height: 59px;
  padding: 0 16px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  border: 1px solid #c9d5df;
  background: linear-gradient(to bottom, #d9e8f2, #c3d2de);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  transition: 0.15s ease;
  user-select: none;
  cursor: pointer;
}
.profilePill:hover {
  filter: brightness(1.02);
}
.person {
  width: 20px;
  height: 20px;
  color: #1f2937;
}

.profileDd {
  right: 0;
  transform-origin: top right;
  margin-top: 8px;
  width: 160px;
  text-align: center;
}
.userName {
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  user-select: none;
}

.menuRow {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 8px;
  font-size: 14px;
  color: #111827;
  border-radius: 10px;
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: 0.15s ease;
}
.menuRow:hover {
  background: #709ebd;
  color: #fff;
}
.logoutIcon {
  width: 16px;
  height: 16px;
}
.menuText {
  margin-left: 4px;
  letter-spacing: 0.5px;
}

/* burger */
.burger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: transparent;
  cursor: pointer;
}
.burger:hover {
  background: #f1f5f9;
}
.burgerIcon {
  width: 24px;
  height: 24px;
}

/* mobile */
.mobile {
  margin-top: 12px;
}
.mobileCard {
  border-radius: 16px;
  padding: 12px;
  border: 1px solid #c9d5df;
  background: linear-gradient(to bottom, #d9e8f2, #c3d2de);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
}
.mobileLink {
  display: block;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #e2e8f0;
  color: #111827;
  font-weight: 600;
  text-decoration: none;
  margin-bottom: 8px;
}
.mobileLink:hover {
  background: rgba(255, 255, 255, 1);
}
.btnReset {
  width: 100%;
  text-align: left;
}
.danger {
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 800;
}

/* responsive */
@media (min-width: 1024px) {
  .center {
    display: flex;
  }
  .right {
    display: flex;
  }
  .burger {
    display: none;
  }
  .mobile {
    display: none;
  }
}
</style>
